import { useEffect, useRef } from 'react'
import { api } from './api.ts'
import { createAvatar } from '@dicebear/core'
import { identicon } from '@dicebear/collection'

interface ProfileImageProps {
  userId: string
  userName: string
  size?: number
  className?: string
  cacheKey?: number
  hasAvatar?: boolean
}

export function ProfileImage({
  userId,
  userName,
  size = 40,
  className = '',
  cacheKey,
  hasAvatar,
}: ProfileImageProps) {
  const imgRef = useRef<HTMLImageElement>(null)
  const initials = userName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  const shouldLoadImage = hasAvatar === true

  let identiconSrc = ''
  if (!shouldLoadImage) {
    try {
      const avatar = createAvatar(identicon, {
        seed: userId || userName,
      })
      const svg = avatar.toString()
      identiconSrc = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
    } catch {
      // Fallback to initials handled in render
    }
  }

  const src = shouldLoadImage
    ? cacheKey != null
      ? `${api.users.getProfileImageUrl(userId)}?t=${cacheKey}`
      : api.users.getProfileImageUrl(userId)
    : identiconSrc

  useEffect(() => {
    if (imgRef.current) {
      const img = imgRef.current
      if (shouldLoadImage) {
        delete img.dataset.fallbackApplied
        img.style.display = 'block'
        const fallback = img.nextElementSibling as HTMLSpanElement
        if (fallback) fallback.style.display = 'none'
        img.src = src
      } else if (identiconSrc) {
        img.style.display = 'block'
        const fallback = img.nextElementSibling as HTMLSpanElement
        if (fallback) fallback.style.display = 'none'
        img.src = identiconSrc
      } else {
        img.style.display = 'none'
        const fallback = img.nextElementSibling as HTMLSpanElement
        if (fallback) fallback.style.display = 'flex'
      }
    }
  }, [userId, src, shouldLoadImage, identiconSrc])

  return (
    <div
      className={`rounded-full bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white font-semibold flex items-center justify-center overflow-hidden ${className}`}
      style={{ width: size, height: size, fontSize: size / 2.5 }}
    >
      {src && (
        <img
          ref={imgRef}
          src={src}
          alt={userName}
          className="w-full h-full object-cover"
          onError={e => {
            const target = e.target as HTMLImageElement
            if (!target.dataset.fallbackApplied && !shouldLoadImage) {
              target.dataset.fallbackApplied = '1'
              target.src = identiconSrc || ''
              if (identiconSrc) {
                return
              }
            }
            target.style.display = 'none'
            const fallback = target.nextElementSibling as HTMLSpanElement
            if (fallback) fallback.style.display = 'flex'
          }}
          onLoad={e => {
            const target = e.target as HTMLImageElement
            target.style.display = 'block'
            const fallback = target.nextElementSibling as HTMLSpanElement
            if (fallback) fallback.style.display = 'none'
          }}
        />
      )}
      <span style={{ display: src ? 'none' : 'flex' }}>{initials}</span>
    </div>
  )
}
